'use client';

import { ExpressCheckoutElement, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import {
	StripeExpressCheckoutElementClickEvent,
	StripeExpressCheckoutElementOptions,
	StripePaymentElementOptions,
} from '@stripe/stripe-js';
import { useActionState, useEffect, useRef, useState, type ReactElement } from 'react';
import { useIntl } from 'react-intl';
import { Temporal } from 'temporal-polyfill';

import { FocusRingVariants, FontSizes, FontWeights, Notice, Text } from '@calm-web/design-system';

import messages from '@/components/purchase/CreditCardForm/messages';
import paymentElementsFormMessages from '@/components/purchase/PaymentElementsForm/messages';
import {
	NameOnCardInput,
	NoticeWrapper,
	PostalCodeInput,
} from '@/components/purchase/PaymentElementsForm/styles';
import defaultMessages from '@/components/purchase/PaymentElementsFormRefresh/messages';
import SecureTransaction from '@/components/purchase/SecureTransaction';
import { useAnalytics } from '@/hooks/analytics';
import useUser from '@/hooks/auth/useUser';
import { useRecaptchaVisibleState } from '@/hooks/store';
import { requiresCustomPostal, usesZipCodeLabel } from '@/utils/payment/customPostalCode';
import { JSONObject } from '@/utils/types';
import { validate } from '@/utils/ui/validator';

import { useGetClientSecret, useSubmitStripePayment } from '../hooks';
import { useSisu } from '../Provider';
import { isFormattedMessage, type MessageNode } from '../utils';
import HelpButton from './HelpButton';
import {
	ExpressCheckoutWrapper,
	PurchaseFormSectionWrapper,
	SectionHeader,
	SubmitButton,
	TextWrapper,
	Wrapper,
} from './styles';

type State =
	| {
			attempts: number;
			status: 'pending';
	  }
	| {
			attempts: number;
			status: 'error';
			error?: string;
	  }
	| {
			attempts: number;
			status: 'success';
	  };

export type PaymentMethodProps = {
	/**
	 * @defaultValue
	 * ```tsx
	 * "sisu-payment-method"
	 * ```
	 */
	'data-testid'?: string;

	analyticsPrefix?: string;

	/**
	 * @defaultValue
	 * ```tsx
	 * <SecureTransaction />
	 * ```
	 */
	secureTransaction?: ReactElement | null;

	title?: MessageNode;

	submitButtonLabel?: MessageNode;

	hideApplePay?: boolean;
};

export default function PaymentMethod({
	'data-testid': testId = 'sisu-payment-method',

	analyticsPrefix = 'Subscribe : Purchase : Form',

	secureTransaction = <SecureTransaction />,
	title,
	submitButtonLabel,

	hideApplePay = false,
}: PaymentMethodProps) {
	const elements = useElements();
	const stripe = useStripe();
	const { user } = useUser();
	const formRef = useRef<HTMLFormElement>(null);
	// Express wallets (Apple Pay / Google Pay) confirm by calling `formRef.requestSubmit()`, which
	// runs the SAME form action as the card Pay button. The wallet supplies its own billing address
	// (incl. postal), so the custom CA/UK/PR postal must NOT be required/injected on that path —
	// this flag lets the action tell an express submit from a card submit (mirrors PEF's
	// `isCardSubmit` gate).
	const isExpressSubmitRef = useRef(false);
	const { formatMessage } = useIntl();
	const { hasFreeTrial, selectedSku, setActiveScreen } = useSisu();
	const { logEvent, logEventAsync } = useAnalytics();
	const recaptchaVisible = useRecaptchaVisibleState();
	const getClientSecret = useGetClientSecret({
		sku: selectedSku.rawSKU,
	});
	const submitStripePayment = useSubmitStripePayment({
		getClientSecret,
	});

	const [nameOnCard, setNameOnCard] = useState('');
	const [isPaymentElementComplete, setIsPaymentElementComplete] = useState(false);
	const [selectedPaymentType, setSelectedPaymentType] = useState<string | null>(null);
	// Clover gap-fill (CA/UK/PR): the v9 deferred PaymentElement no longer auto-collects postal for
	// these countries, so collect it ourselves and inject it into billing_details at submit —
	// otherwise the backend's tax verification rejects with `stripe_invalid_tax_location`. Mirrors
	// PaymentElementsForm via the shared `@/utils/payment/customPostalCode`. Country is seeded from
	// the sku and updated as the buyer changes it in the element; the method type keeps this
	// card-only (bank/Klarna/etc. have their own flows).
	const [billingCountry, setBillingCountry] = useState<string | null>(selectedSku.country ?? null);
	const [paymentMethodType, setPaymentMethodType] = useState('card');
	const [customPostal, setCustomPostal] = useState('');
	const showCustomPostal = requiresCustomPostal(billingCountry) && paymentMethodType === 'card';
	const postalIsValid = !showCustomPostal || customPostal.trim().length > 0;

	const isNameOnCardValid = selectedPaymentType === 'credit_card' ? validate('name', nameOnCard) : true;

	const calmOneId: string | undefined = user?.['https://oneid.calm.com/calm_oneid'];

	const logErrorEvent = (eventProps?: JSONObject) => {
		logEvent({
			eventName: `${analyticsPrefix} : Error`,
			eventProps: {
				currency: selectedSku.currency,
				payment_type: selectedPaymentType,
				is_stripe_next: true,
				...eventProps,
			},
		});
	};

	const [state, formAction, isLoading] = useActionState<State, FormData>(
		async (prevState, formData) => {
			const attempts = (prevState?.attempts ?? 0) + 1;

			const name = formData.get('name')?.toString();

			// Clover gap-fill: for CA/UK/PR CARD, the buyer's postal comes from our custom field —
			// inject it into billing_details so the backend tax check has it. Express/wallet submits
			// (Apple Pay / Google Pay) run this same action but carry their own address, so the
			// custom postal is card-only (mirrors PEF's `isCardSubmit && showCustomPostal`). The card
			// submit button is also gated on `postalIsValid`; this guard is defense-in-depth.
			const isExpressSubmit = isExpressSubmitRef.current;
			isExpressSubmitRef.current = false;
			const collectCustomPostal = !isExpressSubmit && showCustomPostal;
			if (collectCustomPostal && customPostal.trim().length === 0) {
				return {
					attempts,
					status: 'error',
					// Keep the error wording in sync with the field label (PR → "ZIP code").
					error: formatMessage(
						usesZipCodeLabel(billingCountry)
							? paymentElementsFormMessages.postalCodeRequiredPR
							: paymentElementsFormMessages.postalCodeRequired,
					),
				};
			}
			const customBillingAddress = collectCustomPostal
				? { address: { postal_code: customPostal.trim() } }
				: {};

			const { error, isSetupIntent, status } = await submitStripePayment({
				email: user?.email,
				name,
				...customBillingAddress,
			});
			if (error) {
				if ('code' in error && error.code) {
					logErrorEvent({
						error: error.code,
					});
				}
				return { attempts, status: 'error', error: error.message };
			}

			if (status === 'processing') {
				return {
					attempts,
					status: 'error',
					error: formatMessage(paymentElementsFormMessages.processing),
				};
			}

			await logEventAsync({
				eventName: `UA : ${isSetupIntent ? 'Free Trial' : 'Direct Subscription'} Purchase : Completed`,
				eventProps: {
					...(calmOneId && { calm_user_id: calmOneId }),
				},
				logGtmEventOnly: true,
			});

			return { attempts, status: 'success' };
		},
		{ attempts: 0, status: 'pending' },
	);

	useEffect(() => {
		if (state.status === 'success') {
			setActiveScreen('handoff');
		}
	}, [state, setActiveScreen]);

	useEffect(() => {
		if (selectedPaymentType) {
			logEvent({
				eventName: 'Subscribe : Payment Type : Selected',
				eventProps: {
					payment_type: selectedPaymentType,
					is_stripe_next: true,
				},
			});
		}
	}, [logEvent, selectedPaymentType]);

	if (typeof title === 'undefined') {
		title = formatMessage(defaultMessages.paymentMethodHeader);
	}

	if (typeof submitButtonLabel === 'undefined') {
		submitButtonLabel = formatMessage(hasFreeTrial ? messages.startFreeTrial : messages.useCard);
	}

	const paymentElementOptions: StripePaymentElementOptions = {
		layout: 'tabs',
		defaultValues: {
			billingDetails: {
				email: user?.email,
				name: user?.name,
				address: {
					country: selectedSku.country,
				},
			},
		},
	};

	const expressCheckoutElementOptions: StripeExpressCheckoutElementOptions = {
		buttonHeight: 55,
		paymentMethods: {
			applePay: hideApplePay ? 'never' : 'auto',
		},
		billingAddressRequired: true,
	};

	const handleCancelExpressCheckoutElement = () => {
		logEvent({
			eventName: `${analyticsPrefix} : Cancelled`,
			eventProps: {
				payment_type: selectedPaymentType,
				is_stripe_next: true,
			},
		});
	};

	const handleClickExpressCheckoutElement = (event: StripeExpressCheckoutElementClickEvent) => {
		setSelectedPaymentType(event.expressPaymentType);

		// Track payment button click
		logEvent({
			eventName: 'Subscription : Upsell : Clicked',
			eventProps: {
				payment_type: event.expressPaymentType,
				is_stripe_next: true,
			},
		});

		event.resolve({
			...((selectedSku.trialDuration?.days || selectedSku.trialDuration?.months) &&
				process.env.NEXT_PUBLIC_WWW_URL && {
					applePay: {
						deferredPaymentRequest: {
							managementURL: process.env.NEXT_PUBLIC_WWW_URL,
							paymentDescription: formatMessage(paymentElementsFormMessages.applePayPaymentDescription),
							deferredBilling: {
								deferredPaymentDate: new Date(
									Temporal.Now.plainDateISO()
										.add({
											...(selectedSku.trialDuration.days && {
												days: selectedSku.trialDuration.days,
											}),
											...(selectedSku.trialDuration.months && {
												months: selectedSku.trialDuration.months,
											}),
										})
										.toString(),
								),
								label: formatMessage(paymentElementsFormMessages.applePayPaymentDescription),
								amount: 0,
							},
						},
					},
				}),
		});
	};

	const handleConfirmExpressCheckoutElement = () => {
		isExpressSubmitRef.current = true;
		formRef.current?.requestSubmit();
	};

	return (
		<PurchaseFormSectionWrapper data-testid={testId}>
			<SectionHeader>
				{title && (
					<div>
						{isFormattedMessage(title) ? (
							<Text size={FontSizes.lg} weight={FontWeights.Medium}>
								{title}
							</Text>
						) : (
							title
						)}
					</div>
				)}
				{hasFreeTrial && <HelpButton />}
			</SectionHeader>
			<Wrapper action={formAction} id="payment-form" ref={formRef}>
				<ExpressCheckoutWrapper>
					<ExpressCheckoutElement
						options={expressCheckoutElementOptions}
						onCancel={handleCancelExpressCheckoutElement}
						onClick={handleClickExpressCheckoutElement}
						onConfirm={handleConfirmExpressCheckoutElement}
					/>
				</ExpressCheckoutWrapper>
				<TextWrapper>
					<NameOnCardInput
						autoComplete="cc-name"
						error={formatMessage(messages.firstNameError)}
						isValid={isNameOnCardValid}
						label={formatMessage(messages.firstName)}
						labelFocusScaleTransform={0.85}
						name="name"
						onChange={event => {
							setNameOnCard(event.target.value);
						}}
						onFocus={() => {
							setSelectedPaymentType('credit_card');
						}}
						value={nameOnCard}
					/>
					<PaymentElement
						onChange={event => {
							setIsPaymentElementComplete(event.complete);
							// Track method + billing country so the custom postal (a card-billing
							// concern) shows only for card and follows country changes in the element.
							setPaymentMethodType(event.value?.type ?? 'card');
							const country = event.value?.billingDetails?.address?.country;
							if (country) {
								setBillingCountry(country);
							}
						}}
						onFocus={() => {
							setSelectedPaymentType('credit_card');
						}}
						options={paymentElementOptions}
					/>
					{showCustomPostal && (
						<PostalCodeInput
							label={formatMessage(
								usesZipCodeLabel(billingCountry)
									? paymentElementsFormMessages.postalCodeLabelPR
									: paymentElementsFormMessages.postalCodeLabel,
							)}
							name="postalCode"
							type="text"
							value={customPostal}
							onChange={event => setCustomPostal(event.currentTarget.value)}
							onFocus={() => setSelectedPaymentType('credit_card')}
							autoComplete="postal-code"
						/>
					)}
				</TextWrapper>
				<SubmitButton
					backgroundColor="buttonBlue"
					data-testid={`${testId}-submit-button`}
					focusRingVariant={FocusRingVariants.Dark}
					fullWidth
					isDisabled={
						!stripe ||
						!elements ||
						recaptchaVisible ||
						!isNameOnCardValid ||
						!isPaymentElementComplete ||
						!postalIsValid ||
						isLoading
					}
					isLoading={isLoading}
					textColor="white"
					type="submit"
				>
					{submitButtonLabel}
				</SubmitButton>
				{secureTransaction}
				{state.status === 'error' && state.error && (
					<NoticeWrapper>
						<Notice isSuccess={false} describedBy="payment-form">
							{state.error}
						</Notice>
					</NoticeWrapper>
				)}
			</Wrapper>
		</PurchaseFormSectionWrapper>
	);
}
